import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { DocumentFileActionsService } from '../../services/document-file-actions.service';
import { openHistoryDialog } from '../history/history.component';
import { ModuleActionsComponent } from '../interface-actions-buttons';
import { LabelListComponent } from '../label-list.component';
import { DocumentCardComponent } from '../documents/document-card.component';
import type { DocumentCardFile, DocumentCardMenu } from '../documents/document-card.types';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { DETAILS_SERVICE_TOKEN } from '../../../features/admin/modules/modules-entity/templates/details/details/details.service';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';
import { canReadFieldRight, effectiveFieldRightForHierarchy } from '../../../features/admin/modules/field-rights.util';
import { UtilityService } from '../../services/utility.service';
import { UserStore } from '../../../core/services/user.store';
import type { Entry, Module } from '../../../features/admin/modules/modules.model';
import { debugLog } from '../../utils/debug-logger';
import { entryHasFile, resolveDocumentFileName, resolveDocumentTitle } from '../../utils/document-file-meta';
import { CONTAINER_TYPES, formatReviewValue } from './review-value.util';
import {
  deriveCardBadge,
  deriveDocumentPresentation,
  type DocumentCardBadge,
  type DocumentPresentation,
} from '../../utils/document-card-status';

interface AnswerRow {
  label: string;
  value: string;
  fullWidth: boolean;
}

interface AnswerSection {
  label: string;
  rows: AnswerRow[];
}

interface ProcessedPage {
  label: string;
  fields: FormlyFieldConfig[];
  key?: FormlyFieldConfig['key'];
  props?: FormlyFieldConfig['props'];
  className?: FormlyFieldConfig['className'];
  hide?: FormlyFieldConfig['hide'];
  /**
   * Props of the generatedForm-root wrapper field (the wizard/tabs/pages/group container one
   * level above this page) — undefined when this page IS that root-level field (single-page
   * form, no separate wrapper above it). Field-rights are a real admin-configurable surface on
   * that wrapper too (wizard/pages/group/tabs all keep the "Veldrechten per gebruikersrol"
   * toggle — form-builder.component.ts hideRoleBasedRights only hides it for `datatype: 'array'`
   * types), so it must enter the ancestor chain the same way FormService.addExpressionsAndHooks
   * accumulates ancestorProps from the true generatedForm root, not just from this page down.
   */
  wrapperProps?: FormlyFieldConfig['props'];
}

interface ApplicantField {
  label: string;
  value: string | null;
}

/** Minimal shape read from `FormHelper.taxonomyToOptions`' result — enough for the label lookup. */
interface TaxonomyOption {
  label?: string;
  value: { id?: string; slug?: string };
}

/**
 * Field types that never carry a reviewable answer: presentation chrome, action buttons and the
 * checklist (whose template-driven uploads are reviewed through section 3's document cards).
 * Free-form upload fields (`upload-document-entry` etc.) are deliberately NOT in this set: they
 * write into the documents bak, which section 3 (document-records) cannot see, so their answer
 * row here is the reviewer's only view of the submitted file.
 */
const NON_ANSWER_TYPES = new Set([
  'fuse-alert',
  'interface-action-button',
  'divider',
  'title',
  'text',
  'required-documents-checklist',
]);

/**
 * Is this `compareField` an ID reference rather than a keyPath?
 *
 * Both keyPath resolvers this component emulates read against an Entry envelope —
 * `hideConditionalFunction` does `keyPathToValue({ entry: <root form value>, id: undefined }, …)`
 * and falls back to `keyPathToValue(formState.entry, …)` — so every keyPath the form-builder
 * emits is the dotted `entry.…` shape of `props.keyPath`, and the one meaningful bare path is
 * `id`. A single segment that is not `id` can therefore only be a persisted Formly id,
 * reachable ONLY through the live tree's `findFieldValueById`, which the static probe does not
 * have. The 'formly_' prefix is NOT the discriminant: the form-builder keeps author-set ids
 * verbatim, and such an id used to slip past and be evaluated as a keyPath — resolving to
 * undefined and hiding an answer the applicant really gave.
 */
function isIdReference(compareField: string): boolean {
  const normalized = UtilityService.normalizeKeyPath(compareField);
  return !normalized.includes('.') && normalized !== 'id';
}

const DOCUMENTS_PAGE_KEY = 'documenten';
const DOCUMENT_MODULE_CODE = 'document-records';
const ONBOARDING_ENTRY_MODULE_CODE = 'onboarding-entry';
const ONBOARDING_FORMS_MODULE_CODE = 'onboarding-forms';

/**
 * Onboarding overview field — renders three explicit sections inside an
 * onboarding-aanmelding detail-view:
 *
 *   1. **Aanvrager** — applicant identity from the parent aanmelding (read via
 *      `formState.aanmelding`). Dense definition-list, scannable in 2 seconds.
 *   2. **Onboarding-formulier** — the wizard answers the applicant filled in,
 *      grouped by wizard-page label, EXCLUDING the documenten-page (which is
 *      surfaced separately as section 3).
 *   3. **Documenten** — the wizard's own documenten-page (with its
 *      `required-documents-checklist` field) rendered in readonly form so the
 *      admin sees the same uploads the applicant submitted, with the same
 *      proven loading logic that the public-side wizard already uses.
 *
 * The component still registers under the `readonly_onboarding` field-type
 * key for backwards-compatibility with existing dd-config; only the
 * dropdown-label is renamed to "Onboarding-overzicht".
 *
 * Parent panel must pass `formState.aanmelding` for section 1 to populate.
 */
@Component({
  selector: 'formly-readonly-onboarding',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatProgressSpinnerModule, ModuleActionsComponent, LabelListComponent, DocumentCardComponent],
  template: `
    <!-- Onboarding-review "Edit card" (Figma contract §2: bg-card, rounded-xl, p-7/p-6, no
         shadow). Container→Slot gap 20px (§2), section gap 24px (§3) — the accordion/<details>
         interaction is gone; every section is always open. -->
    <div class="w-full bg-card rounded-xl py-7 px-6 flex flex-col gap-5" data-cy="review-card">
      <div class="flex items-center gap-3">
        <span
          class="inline-flex items-center justify-center flex-none w-[1.875rem] h-[1.875rem] rounded-full bg-[var(--dd-badge-accent-bg)]"
          aria-hidden="true">
          <mat-icon
            class="icon-size-4 text-[color:var(--dd-badge-accent-fg)]"
            svgIcon="heroicons_outline:user"></mat-icon>
        </span>
        <span class="text-[0.9375rem] font-medium leading-[1.3] text-default">Onboarding details</span>
      </div>

      <div class="flex flex-col gap-6">
        <!-- Section 1: Aanvrager — Label list grid (contract §4), 2 columns / 20px gap / 12px row-gap. -->
        <section class="flex flex-col gap-3" data-cy="onboarding-overview-section-aanvrager">
          <h4 class="text-base font-medium leading-none text-[color:var(--dd-accent)] m-0">Aanvrager</h4>
          @if (applicantFields().length === 0) {
            <p class="text-[0.8125rem] text-accent m-0 italic" data-cy="review-empty">Geen aanvrager-gegevens beschikbaar.</p>
          } @else {
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
              @for (f of applicantFields(); track f.label) {
                <dd-label-list data-cy="review-row" [label]="f.label" [value]="f.value"></dd-label-list>
              }
            </div>
          }
        </section>

        <!-- Section 2: Onboarding-formulier — wizard answers as Label-list rows (contract §4),
             same grid as the Aanvrager section. Only answered fields render: an empty answer is
             skipped rather than shown as a dead "—" row, so a half-filled form stays compact and
             an untouched form falls through to the empty-state text below. -->
        <section class="flex flex-col gap-3" data-cy="onboarding-overview-section-formulier">
          <h4 class="text-base font-medium leading-none text-[color:var(--dd-accent)] m-0">Onboarding-formulier</h4>
          @if (isLoadingForm()) {
            <div class="flex items-center gap-3 py-2">
              <mat-spinner diameter="20"></mat-spinner>
              <span class="text-[0.8125rem] text-accent">Antwoorden laden…</span>
            </div>
          } @else if (answerSections().length === 0) {
            <p class="text-[0.8125rem] text-accent m-0 italic" data-cy="review-empty">
              De vervoerder heeft het formulier nog niet ingevuld of de gegevens zijn niet meer beschikbaar.
            </p>
          } @else {
            <div class="flex flex-col gap-5">
              @for (section of answerSections(); track $index) {
                <div class="flex flex-col gap-3">
                  @if (section.label) {
                    <h5
                      class="text-[0.75rem] font-medium uppercase tracking-[0.08em] text-[color:var(--dd-accent)] m-0">
                      {{ section.label }}
                    </h5>
                  }
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
                    @for (row of section.rows; track $index) {
                      <dd-label-list
                        data-cy="review-row"
                        [label]="row.label"
                        [value]="row.value"
                        [fullWidth]="row.fullWidth"
                        [multiline]="row.fullWidth"></dd-label-list>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </section>

        <!-- Section 3: Documenten — renders the SHARED app-document-card (#21036) instead of a
             hand-rolled card, so this surface stays in lockstep with the checklist and the
             documents-cards entity template. The status badge is therefore the family's pill
             app-status-badge (via the card's badge input), not the earlier dot-indicator — a
             deliberate style change, not an oversight: the dot was this surface's own one-off,
             the pill is what every other document-card consumer already renders. Goedkeuren /
             Afwijzen has no place on the shared contract, so it is projected into the card's
             documentCardActions extension slot instead. -->
        <section class="flex flex-col gap-3" data-cy="onboarding-overview-section-documenten">
          <div class="flex items-center gap-2">
            <h4 class="text-base font-medium leading-none text-[color:var(--dd-accent)] m-0">Documenten</h4>
            @if (documents().length > 0) {
              <span class="text-[0.75rem] text-[color:var(--dd-accent)] tabular-nums">
                · {{ documentsCounter().reviewed }} / {{ documentsCounter().total }} beoordeeld
              </span>
            }
          </div>
          @if (documentsLoading()) {
            <div class="flex items-center justify-center gap-3 py-8">
              <mat-spinner diameter="20"></mat-spinner>
              <span class="text-[0.8125rem] text-accent">Documenten laden…</span>
            </div>
          } @else if (documents().length === 0) {
            <p class="text-[0.8125rem] text-accent text-center m-0 italic py-6" data-cy="review-empty">Geen documenten geüpload.</p>
          } @else {
            <ul class="m-0 flex list-none flex-col gap-4 p-0" role="list">
              @for (doc of documents(); track doc.id) {
                <li>
                  <app-document-card
                    data-cy="review-document-card"
                    [title]="documentTitle(doc)"
                    [badge]="documentBadge(doc)"
                    [reviewComment]="documentReviewComment(doc)"
                    [file]="documentCardFile(doc)"
                    [menu]="documentCardMenu(doc)"
                    [dataCy]="'onboarding-overview-doc-' + doc.id"
                    (preview)="previewDocument(doc)"
                    (download)="downloadDocument(doc)"
                    (history)="openDocumentHistory(doc)"
                    (archive)="archiveDocument(doc)">
                    @if (documentModuleId(); as docModuleId) {
                      <app-module-interface-actions-buttons
                        documentCardActions
                        [moduleId]="docModuleId"
                        [entries]="[doc]"
                        [form]="dummyDocForm"
                        [context]="'overview'"
                        [buttonSize]="'small'"
                        [attr.data-cy]="'onboarding-overview-doc-actions-' + doc.id">
                      </app-module-interface-actions-buttons>
                    }
                  </app-document-card>
                </li>
              }
            </ul>
          }
        </section>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }
  `,
})
export class FormlyReadonlyOnboardingComponent extends FieldType implements OnInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  /** Only for `hideConditionalFunction` — the answer rows must honour conditional hides. */
  readonly #formService = inject(FormService);
  readonly #utilityService = inject(UtilityService);
  readonly #documentActions = inject(DocumentFileActionsService);
  readonly #dialog = inject(MatDialog);
  readonly #userStore = inject(UserStore);
  readonly #destroyRef = inject(DestroyRef);

  /**
   * Memoized last-pushed pending-count zodat hetzelfde getal niet bij iedere
   * documents-snapshot opnieuw naar de BE gepatcht wordt (zou anders een
   * patch-loop kunnen geven bij re-render).
   */
  readonly #lastSyncedPendingCount = signal<number | null>(null);
  /** A document count is authoritative only after the current exact-user request succeeded. */
  readonly #documentsLoadSucceeded = signal(false);
  /** Invalidates callbacks from a request superseded by a later aanmelding/user binding. */
  #documentsRequestGeneration = 0;

  constructor() {
    super();
    // Diagnostic — track what the store actually holds for documenten so
    // we can see whether loadEntries populated it or it stays empty.
    effect(() => {
      const moduleId = this.documentModuleId();
      if (!moduleId) return;
      const all = this.#modulesStore.entries()[moduleId] ?? [];
      debugLog('OnboardingOverview', 'store.documenten.snapshot', {
        moduleId,
        storedCount: all.length,
        storedIds: all.map(e => e.id).slice(0, 10),
        scopeUserId: this.#docsScopeUserId(),
        afterFilter: this.documents().length,
      });
    });

    // Sync pending-docs-count naar de aanmelding wanneer admin de detail-page opent.
    // Goedkeuren-IA conditie eist `entry.pending-docs-count == 0`; de count wordt nergens
    // anders gezet (user-flow heeft geen write-access, BE-action-cascade is bewust
    // verwijderd). De `readonly_onboarding`-component IS de admin-context die de
    // aanmelding + documents beide ziet — daar hoort de sync thuis.
    //
    // Definitie: `pending = total - approved`. Rejected telt mee als pending zodat
    // admin eerst die docs moet oplossen (vervangen via user, of de aanmelding
    // afkeuren) voordat Goedkeuren verschijnt.
    effect(() => {
      const aanmelding = this.aanmelding();
      if (!aanmelding?.id || !aanmelding.moduleId) return;
      if (!this.#documentsLoadSucceeded()) return;
      const docs = this.documents();

      const pending = docs.filter(doc => this.documentPresentation(doc).blocksOnboarding).length;

      if (this.#lastSyncedPendingCount() === pending) return;
      const current = aanmelding.entry?.['pending-docs-count'];
      if (current === pending) {
        this.#lastSyncedPendingCount.set(pending);
        return;
      }
      this.#lastSyncedPendingCount.set(pending);
      this.#entriesService
        .patchEntry(aanmelding.moduleId, aanmelding.id, { entry: { 'pending-docs-count': pending } })
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: patched => {
            // Carry the etag the server just handed back.
            //
            // Cosmos bumps the etag on every write, and this used to spread `...aanmelding` — the
            // etag from BEFORE the patch. The store then held a stale one, and the interface
            // actions read their etag straight from the store
            // (interface-actions.service.ts: "Get the latest etag from the store before editing").
            // Result, reported by a user on 2026-09-03: approve the documents, then approve the
            // aanmelding, and the save comes back 412. The store's rebase-retry does cover it, but
            // at the price of an extra GET+PUT per click and a conflict dialog about a field the
            // user never touched. Reading the etag off the response removes the conflict instead
            // of recovering from it.
            const freshEtag = patched?._etag ?? patched?.etag;
            const updatedAanmelding: Entry = {
              ...aanmelding,
              ...(freshEtag ? { _etag: freshEtag, etag: freshEtag } : {}),
              entry: {
                archived: aanmelding.entry?.archived ?? false,
                ...(aanmelding.entry ?? {}),
                'pending-docs-count': pending,
              },
            };
            this.#modulesStore.updateEntryState({
              moduleId: aanmelding.moduleId,
              entry: updatedAanmelding,
            });
            this.#syncPendingCountToDetailsForm(updatedAanmelding, pending);
          },
          error: err => {
            console.error('Sync pending-docs-count vanuit readonly-onboarding mislukt:', err);
            this.#utilityService.notify('Documentstatus bijwerken mislukt — probeer opnieuw.');
          },
        });
    });
  }

  /**
   * DetailsService merges the live form value over its store-backed entry in `formState`.
   * Keep an existing technical count-control aligned after the PATCH, otherwise its stale
   * default wins and detail action conditions keep evaluating the old count. Programmatic
   * `setValue` emits the model update but does not mark the control or form dirty.
   */
  #syncPendingCountToDetailsForm(aanmelding: Entry, pending: number): void {
    const detailsEntry = this.#detailsService?.entry();
    if (detailsEntry?.id !== aanmelding.id || detailsEntry.moduleId !== aanmelding.moduleId) return;

    const pendingControl = this.#detailsService?.form().get('pending-docs-count');
    if (!pendingControl || pendingControl.value === pending) return;
    pendingControl.setValue(pending);
  }

  /* ── Signals seeded from Formly properties in ngOnInit ────────────────── */

  /**
   * Mirrors of Formly's non-reactive properties. `field`/`options`/`formControl`
   * become available only after the FieldType is wired by Formly, so reading
   * them inside `computed()` once would snapshot `null` and never update.
   * We snapshot them in `ngOnInit` and expose as signals.
   */
  readonly #aanmeldingState = signal<Entry | null>(null);
  readonly #onboardingValue = signal<Entry | null>(null);

  /**
   * Detail-page context, when present. On the aanmelding detail page the field is generated
   * from the module's `generatedForm`, which cannot carry an `aanmelding` prop — the envelope
   * lives in the page's DetailsService instead (same optional-token pattern as tabs/route/
   * inline-entries). Null in the review-inbox panel, where props.aanmelding is the source.
   */
  readonly #detailsService = inject(DETAILS_SERVICE_TOKEN, { optional: true });

  /* ── Section 1: Aanvrager ─────────────────────────────────────────────── */

  readonly aanmelding = computed<Entry | null>(() => {
    const snapshot = this.#aanmeldingState() ?? this.#detailsService?.entry() ?? null;
    if (!snapshot?.id || !snapshot.moduleId) return snapshot;
    return this.#modulesStore.entries()[snapshot.moduleId]?.find(entry => entry.id === snapshot.id) ?? snapshot;
  });

  readonly applicantFields = computed<ApplicantField[]>(() => {
    const e = this.aanmelding()?.entry;
    if (!e) return [];
    const user = e['user'];
    return [
      { label: 'Naam', value: user?.entry?.general?.name?.fullName ?? user?.selectedInput ?? null },
      { label: 'E-mail', value: user?.entry?.general?.email ?? e['emailAdress'] ?? null },
      { label: 'Bedrijf', value: e['company']?.selectedInput ?? null },
      { label: 'Vestiging', value: e['location']?.selectedInput ?? null },
      { label: 'Onboarding-formulier', value: e['onboarding-form']?.selectedInput ?? null },
      { label: 'Contract', value: e['contract']?.selectedInput ?? null },
      { label: 'Status', value: e['status']?.name ?? null },
    ];
  });

  /* ── Section 2 & 3: Wizard pages ──────────────────────────────────────── */

  readonly onboardingEntry = computed<Entry | null>(() => this.#onboardingValue());

  /**
   * `generatedForm` fetched separately from the onboarding-forms module, used when the
   * onboarding entry only carries a thin `{ id, selectedInput }` reference. Null while the
   * embedded envelope is usable (or before a fetch resolves).
   */
  readonly #fetchedGeneratedForm = signal<FormlyFieldConfig[] | null>(null);
  readonly #formFetchState = signal<'idle' | 'loading' | 'failed'>('idle');
  /** Id whose fetch already ran (or is in flight) — keeps re-binds from re-fetching. */
  readonly #fetchedFormId = signal<string | null>(null);
  /** Aanmelding whose reverse onboarding-entry lookup already ran (or is in flight). */
  readonly #lookedUpAanmeldingId = signal<string | null>(null);

  /** True while we have an id but the inner `entry` payload hasn't arrived yet. */
  readonly isLoadingForm = computed<boolean>(() => {
    const entry = this.onboardingEntry();
    if (!!entry?.id && !entry.entry) return true;
    return this.#formFetchState() === 'loading';
  });

  /**
   * Raw wizard pages from the form definition. Computed once and partitioned downstream into
   * answerSections (non-documenten) — the documenten-page is surfaced separately as section 3.
   */
  /**
   * Raw persisted form definition. The reference is an envelope with the full
   * onboarding-forms entry on some aanmeldingen and a thin `{ id, selectedInput }` on others
   * (#20715) — fall back to the separately fetched definition when the envelope is missing.
   */
  readonly #generatedFormSource = computed<FormlyFieldConfig[]>(() => {
    const entry = this.onboardingEntry();
    const embedded = entry?.entry?.['onboarding-form']?.entry?.form?.generatedForm;
    const generatedForm = Array.isArray(embedded) && embedded.length > 0 ? embedded : this.#fetchedGeneratedForm();
    return Array.isArray(generatedForm) ? generatedForm : [];
  });

  /**
   * Persisted Formly field id → props.keyPath, for translating legacy id-style
   * `compareField`s ('formly_…', the shape findFieldValueById resolves on the LIVE field
   * tree) into keyPaths the static walk can evaluate against the entry.
   */
  readonly #conditionKeyPathById = computed<Map<string, string>>(() => {
    const byId = new Map<string, string>();
    const walk = (fields: FormlyFieldConfig[]): void => {
      for (const field of fields) {
        if (!field || typeof field !== 'object') continue;
        const keyPath = field.props?.keyPath;
        // FIRST-wins on duplicate ids, mirroring searchFieldGroupsForValue: sub-id
        // composition and copied source fields make duplicates structurally expected, and a
        // last-wins map would evaluate against a different field than the live form does.
        // fieldArray is deliberately NOT walked — the live resolver never descends it, so an
        // id only reachable there must stay unresolvable here too (fail-open downstream).
        if (typeof field.id === 'string' && field.id && typeof keyPath === 'string' && keyPath && !byId.has(field.id)) {
          byId.set(field.id, keyPath);
        }
        if (Array.isArray(field.fieldGroup)) walk(field.fieldGroup);
      }
    };
    walk(this.#generatedFormSource());
    return byId;
  });

  readonly #processedPages = computed<ProcessedPage[]>(() => {
    const generatedForm = this.#generatedFormSource();
    if (generatedForm.length === 0) return [];

    // The persisted definition is data, not a type: a legacy or half-migrated document can carry
    // a `fieldGroup` that is not an array or a null element. Skip those instead of throwing —
    // a throw inside a computed is cached by Angular and re-thrown on every read, which takes
    // the whole review card down for that entry.
    const pages: ProcessedPage[] = [];
    for (const field of generatedForm) {
      if (!field || typeof field !== 'object') continue;
      // Single-wizard top-level case: walk into its pages.
      if (Array.isArray(field.fieldGroup)) {
        // If the wrapper itself has a label and no nested wizard pages, treat it
        // as one section.
        const innerHasPages = field.fieldGroup.some(p => Array.isArray(p?.fieldGroup) && p.props?.label);
        if (innerHasPages) {
          for (const page of field.fieldGroup) {
            if (Array.isArray(page?.fieldGroup)) {
              pages.push({
                label: String(page.props?.label ?? ''),
                fields: page.fieldGroup,
                key: page.key,
                props: page.props,
                className: page.className,
                hide: page.hide,
                // `field` is the root-level wrapper (wizard/tabs/pages/group) one level above
                // every page it carries.
                wrapperProps: field.props,
              });
            }
          }
        } else if (field.props?.label) {
          pages.push({
            label: String(field.props.label),
            fields: field.fieldGroup,
            key: field.key,
            props: field.props,
            className: field.className,
            hide: field.hide,
            // `field` IS the root-level entry here — no separate wrapper above it.
          });
        }
      }
    }
    return pages;
  });

  /**
   * Wizard pages excluding the documenten-page, flattened to answered Label-list rows
   * (Figma contract §4 — same presentation as the Aanvrager section). Unanswered fields and
   * pages without any answer are dropped entirely; "—" placeholder rows would bury the actual
   * answers under form-shaped noise on partially filled aanmeldingen.
   */
  readonly answerSections = computed<AnswerSection[]>(() => {
    const entry = this.onboardingEntry();
    const currentRoleIds = this.#userStore.currentUserRoles();
    // Scoped to this one computation only — taxonomies can change between recomputes, so the
    // resolved options must never survive past this run (see #taxonomyOptionResolver).
    const taxonomyOptionsCache = new Map<string, TaxonomyOption[]>();
    return (
      this.#processedPages()
        .filter(p => !this.#isDocumentsPage(p))
        // A step hidden the way technical helper fields are hidden (hide/className/width) was
        // never shown to the applicant — same rules as the field walk below applies.
        .filter(p => !this.#isTechnicallyHiddenPage(p))
        // The live wizard hides conditionally configured STEPS too — a hidden step's stale
        // answers must not read as real answers, same rule as per field below. Page
        // conditions evaluate CONSERVATIVELY (pageLevel): a dropped section is strictly
        // worse than a dropped row.
        .filter(p => !this.#hiddenByCondition({ props: p.props } as FormlyFieldConfig, entry, true))
        .map(p => ({
          label: p.label,
          // The wrapper's and the page's own props seed the ancestor chain, mirroring
          // FormService.addExpressionsAndHooks/iterateFields, which accumulates ancestorProps
          // from the true generatedForm root: a role-based right denied on the wizard/tabs/pages/
          // group wrapper, OR on the page itself, must flow down to every field on it — the same
          // way it flows into a nested group below.
          rows: this.#extractAnswerRows(p.fields, entry, currentRoleIds, [p.wrapperProps, p.props], taxonomyOptionsCache),
        }))
        .filter(s => s.rows.length > 0)
    );
  });

  #isTechnicallyHiddenPage(page: ProcessedPage): boolean {
    const className = typeof page.className === 'string' ? page.className : '';
    return page.hide === true || className.includes('hidden') || page.props?.width === 'hidden';
  }

  #isDocumentsPage(page: { label: string; key?: FormlyFieldConfig['key'] }): boolean {
    const key = typeof page.key === 'string' ? page.key.toLowerCase() : '';
    if (key === DOCUMENTS_PAGE_KEY) return true;
    return page.label.toLowerCase() === 'documenten';
  }

  /**
   * Label lookup for taxonomy-backed option sets. The saved generatedForm carries no options
   * for those (FormService strips optionsArray-backed fields to `options: []` before persisting),
   * and this card walks the saved form instead of the runtime-refilled one — so a taxonomy
   * answer stored as a bare slug/id would otherwise render as that raw token. Resolution stays
   * UNFILTERED on purpose (FormHelper.filterTaxonomyOptions is for the pick-list): an answer
   * that has since been excluded from the field still has to read as what it was.
   * Module-backed sets are not resolved here; their answers are stored as linked-entry
   * objects, which the shared formatter labels from `selectedInput`/`name`.
   *
   * `optionsCache` is keyed by `ownerModuleId|taxonomyId` and lives only for the caller's ONE
   * `answerSections` computation — a multi-select answer recurses this resolver per item, and
   * other fields on the same page can share the same taxonomy, so without the cache
   * `FormHelper.taxonomyToOptions` (a full array rebuild) reran for every value lookup instead of
   * once per distinct taxonomy. The owner module must be part of the key: two fields can share a
   * `taxonomyId` while pointing at different owner modules (different `moduleCode`), and keying on
   * `taxonomyId` alone would let the second field reuse the first's resolved (or empty, if the
   * first owner failed to resolve) options.
   */
  #taxonomyOptionResolver(
    optionsArray: unknown,
    optionsCache: Map<string, TaxonomyOption[]>
  ): ((value: string) => string | null) | undefined {
    if (!Array.isArray(optionsArray)) return undefined;
    const taxonomySets = optionsArray.filter(
      (set): set is { taxonomyId?: string; moduleCode?: string } =>
        !!set && typeof set === 'object' && (set as { optionsType?: string }).optionsType === 'taxonomy'
    );
    if (taxonomySets.length === 0) return undefined;

    return (value: string): string | null => {
      const taxonomies = this.#modulesStore.taxonomies();
      const moduleCodeMapper = this.#modulesStore.moduleCodeMapper();
      for (const set of taxonomySets) {
        const ownerModuleId = set.moduleCode ? moduleCodeMapper[set.moduleCode]?.id : undefined;
        const cacheKey = `${ownerModuleId ?? ''}|${set.taxonomyId ?? ''}`;
        let options = optionsCache.get(cacheKey);
        if (!options) {
          options = FormHelper.taxonomyToOptions(taxonomies, ownerModuleId, set.taxonomyId);
          optionsCache.set(cacheKey, options);
        }
        const match = options.find(option => option.value.slug === value || option.value.id === value);
        if (match?.label) return match.label;
      }
      return null;
    };
  }

  /**
   * Flatten a wizard page's fields into answered {label, value} rows, walking nested groups.
   *
   * `ancestorProps` is the chain of `props` objects from the page down to (but excluding) the
   * current field — the same accumulator `FormService.addExpressionsAndHooks`/`mergedHideFunction`
   * build while walking `fieldGroup`/`fieldArray`. It lets `effectiveFieldRightForHierarchy` narrow
   * a role-based read-right the same way the live form does: a right denied on a parent group
   * flows down to every field inside it, even one with no `hasRoleBasedRights` config of its own.
   */
  #extractAnswerRows(
    fields: FormlyFieldConfig[],
    entry: Entry | null,
    currentRoleIds: readonly string[],
    ancestorProps: readonly unknown[],
    taxonomyOptionsCache: Map<string, TaxonomyOption[]>
  ): AnswerRow[] {
    const rows: AnswerRow[] = [];
    for (const field of fields) {
      if (!field || typeof field !== 'object') continue;
      const type = String(field.type ?? '');
      if (NON_ANSWER_TYPES.has(type)) continue;

      // Fields the applicant never saw (technical helpers hidden via class or width) have no
      // place in the review either.
      const className = typeof field.className === 'string' ? field.className : '';
      const width = field.props?.width;
      if (field.hide === true || className.includes('hidden') || width === 'hidden') continue;

      // The live form hides conditional fields via runtime expressions the persisted form cannot
      // carry; evaluate the same persisted conditional/conditions props against the entry here,
      // so a stale answer from an inactive wizard branch never reads as a real answer.
      if (this.#hiddenByCondition(field, entry)) continue;

      const fieldAncestorProps = [...ancestorProps, field.props];

      // Re-apply the live form's per-field role-based read right: the pre-rewrite component ran
      // the generated form through FormService.addExpressionsAndHooks, whose `hide` expression
      // (mergedHideFunction) hides a field the current user's roles may not read. This static walk
      // must fail closed the same way, or a field marked read-restricted in the form builder
      // renders its answer to every admin opening this card.
      if (!canReadFieldRight(effectiveFieldRightForHierarchy(fieldAncestorProps, currentRoleIds))) continue;

      const label = this.#fieldLabel(field);

      // Recurse only into layout containers and unlabeled wrappers (wizard-review parity via
      // the shared CONTAINER_TYPES). A LABELED non-container field with a fieldGroup is a
      // composite LEAF — name, address, date_range, geolocation, … — one row, never one per
      // sub-key; generatedForm grows fieldGroups on eleven such leaf types, so an allow-list
      // of composites can never be complete.
      if (Array.isArray(field.fieldGroup) && field.fieldGroup.length && (!label || CONTAINER_TYPES.has(field.type as string))) {
        rows.push(...this.#extractAnswerRows(field.fieldGroup, entry, currentRoleIds, fieldAncestorProps, taxonomyOptionsCache));
        continue;
      }

      if (!label) continue;

      const raw = this.#fieldValue(field, entry);
      // Masking of secret types happens inside the shared formatter.
      const value = formatReviewValue(raw, {
        type,
        options: field.props?.options,
        optionsArray: field.props?.optionsArray,
        resolveOptionLabel: this.#taxonomyOptionResolver(field.props?.optionsArray, taxonomyOptionsCache),
      });
      if (value === null) continue;

      const fullWidth = type === 'textarea' || value.length > 48;
      rows.push({ label, value, fullWidth });
    }
    return rows;
  }

  /**
   * Mirrors the live form's conditional hide for the static walk: FormService's
   * `hideConditionalFunction` is reused verbatim on a minimal probe whose root-form value is the
   * onboarding entry's data — the same value the live expression reads (`getValueOfRootForm`
   * plus the `formState.entry` fallback). Fields without the persisted conditional/conditions
   * props are never hidden here.
   */
  #hiddenByCondition(field: FormlyFieldConfig, entry: Entry | null, pageLevel = false): boolean {
    if (field.props?.conditional !== true || !Array.isArray(field.props?.conditions)) return false;

    // Legacy conditions name their compare field by persisted Formly id — the live form
    // resolves those via findFieldValueById on the real field tree, which the probe does not
    // have. Any id the map knows translates to the target field's keyPath; a reference this
    // form does not carry AND that no keyPath resolver could ever reach fails OPEN (show the
    // row): a stray extra row beats silently dropping an answer the applicant gave from an
    // approval surface. `isIdReference` is the discriminant rather than the 'formly_'
    // prefix, because nothing enforces that prefix — the form-builder keeps author-set ids
    // verbatim, and one of those left the fail-open branch and was evaluated as a keyPath.
    const byId = this.#conditionKeyPathById();
    const conditions: unknown[] = [];
    for (const condition of field.props.conditions as { compareField?: unknown }[]) {
      const rawCompareField = condition?.compareField;
      const compareField =
        typeof rawCompareField === 'string' && byId.has(rawCompareField) ? byId.get(rawCompareField) : rawCompareField;
      if (compareField !== rawCompareField) {
        conditions.push({ ...condition, compareField });
      } else {
        if (typeof compareField === 'string' && isIdReference(compareField)) {
          return false;
        }
        conditions.push(condition);
      }

      // Page level is CONSERVATIVE: only a compare value that resolves from the entry's own
      // DATA (a persisted answer — stable between fill time and review time) may hide a whole
      // section. An envelope field like `status` only resolves through the formState.entry
      // fallback against the CURRENT entry, while the live wizard read the FILL-TIME entry —
      // hiding on that drift silently drops every answer the applicant gave on the step. The
      // row-level filter inside the section still prunes stale per-field answers.
      if (pageLevel) {
        const resolvesFromData =
          typeof compareField === 'string' &&
          compareField &&
          UtilityService.keyPathToValue(
            { entry: entry?.entry ?? {}, id: entry?.id } as unknown as Entry,
            compareField
          ) !== undefined;
        if (!resolvesFromData) {
          return false;
        }
      }
    }

    const probe = {
      props: { ...field.props, conditions },
      form: { getRawValue: () => entry?.entry ?? {} },
      options: { formState: { entry } },
    } as unknown as FormlyFieldConfig;
    try {
      return this.#formService.hideConditionalFunction()(probe);
    } catch (conditionError) {
      // Fail OPEN: a malformed condition must degrade to one extra row, never blank the whole
      // card — a throw inside the answerSections computed is cached by Angular and re-thrown
      // on every read.
      debugLog('OnboardingOverview', 'conditional evaluation failed, showing the row', conditionError);
      return false;
    }
  }

  #fieldLabel(field: FormlyFieldConfig): string | null {
    const label = field.props?.label;
    if (typeof label !== 'string') return null;
    // Rich labels (e.g. the terms checkbox) can carry HTML — the Label-list row wants plain text.
    const plain = label.replace(/<[^>]*>/g, '').trim();
    return plain || null;
  }

  #fieldValue(field: FormlyFieldConfig, entry: Entry | null): unknown {
    if (!entry) return undefined;
    const keyPath = field.props?.keyPath;
    if (typeof keyPath === 'string' && keyPath) {
      return UtilityService.keyPathToValue(entry, keyPath);
    }
    const key = field.key;
    return typeof key === 'string' ? entry.entry?.[key] : undefined;
  }

  /* ── Section 3: Documenten (store-based, like NotificationsService) ───── */

  /** User id used to scope the store-wide document-record cache. */
  readonly #docsScopeUserId = signal<string | null>(null);
  readonly documentsLoading = signal<boolean>(false);

  /** Empty placeholder form-group required by `<app-module-interface-actions-buttons>`. */
  readonly dummyDocForm = new FormGroup({});

  /**
   * Documents read directly from `ModulesStore.entries()[documentModuleId]` —
   * the store is populated by `loadEntries` with the same OData filter we
   * use server-side. We additionally filter client-side here so that any
   * cached entries from unrelated contexts (e.g. another admin viewing a
   * different aanmelding earlier in the session) don't leak through.
   */
  readonly documents = computed<Entry[]>(() => {
    const moduleId = this.documentModuleId();
    if (!moduleId) return [];
    const all = this.#modulesStore.entries()[moduleId] ?? [];
    const userId = this.#docsScopeUserId();
    if (!userId) return [];
    return all.filter(d => {
      // The records module is homogeneous; scope the review to records that
      // belong to this user and originate from a configured template.
      if (!d.entry?.['template-source']?.id) return false;
      return d.entry?.['user']?.id === userId;
    });
  });

  readonly documentsCounter = computed(() => {
    const docs = this.documents();
    return {
      total: docs.length,
      reviewed: docs.filter(d => {
        const reviewState = this.documentPresentation(d).reviewState;
        return reviewState === 'approved' || reviewState === 'rejected';
      }).length,
    };
  });

  /**
   * A `computed`, not a method: the template reads this once per document row (directly, and
   * again through `documentCardMenu` → `canArchiveDocument`), and a method re-runs the
   * `enabledEntities()` scan on every change-detection pass. The module list only changes when
   * the store reloads, so memoization collapses the whole loop to one lookup.
   */
  readonly documentModuleId = computed<Module['id'] | null>(
    () => this.#modulesStore.enabledEntities().find(m => m.code === DOCUMENT_MODULE_CODE)?.id ?? null
  );

  #loadDocuments(userId: string, generation: number): void {
    const moduleId = this.documentModuleId();
    if (!moduleId) {
      debugLog('OnboardingOverview', 'documents.fetch.skipped', 'no document-records module id');
      return;
    }
    const filter = `entry.user.id eq '${userId}' and entry.archived eq false`;
    debugLog('OnboardingOverview', 'documents.fetch.start', { moduleId, filter });

    // Dispatch through the store (same pattern as NotificationsService) — the
    // store handles the request, populates `entries()[moduleId]`, and our
    // `documents` computed reads from there with a client-side scope-filter.
    this.documentsLoading.set(true);
    this.#modulesStore.loadEntries({
      moduleId,
      offset: 0,
      limit: 50,
      filter,
      applyQueryParams: false,
      onSettled: result => {
        if (generation !== this.#documentsRequestGeneration) return;
        this.documentsLoading.set(false);
        this.#documentsLoadSucceeded.set(result === 'loaded' || result === 'skipped');
      },
    });
  }

  /** Unify status-slug across data shapes. Missing status = 'pending'. */
  #documentStatusSlug(doc: Entry): 'approved' | 'rejected' | 'expired' | 'pending' {
    const slug = doc.entry?.['status']?.slug;
    if (slug === 'approved' || slug === 'rejected' || slug === 'expired') return slug;
    return 'pending';
  }

  documentTitle(doc: Entry): string {
    return resolveDocumentTitle(doc);
  }

  documentPresentation(doc: Entry): DocumentPresentation {
    return deriveDocumentPresentation({
      state: this.#documentStatusSlug(doc),
      endDate: doc.entry?.['end-date'] ?? null,
      hasFile: entryHasFile(doc),
    });
  }

  documentBadge(doc: Entry): DocumentCardBadge {
    return deriveCardBadge({
      state: this.#documentStatusSlug(doc),
      endDate: (doc.entry?.['end-date'] as string | null | undefined) ?? null,
    });
  }

  documentReviewComment(doc: Entry): string | null {
    return (doc.entry?.['review-comment'] as string | undefined) ?? null;
  }

  /**
   * Feeds `app-document-card`'s `[file]` input — name plus upload moment, which the card renders
   * as its own "Geüpload dd-MM-yy" meta line. Closes the earlier deviation where this surface's
   * own meta line showed the filename instead of the upload date.
   */
  documentCardFile(doc: Entry): DocumentCardFile | null {
    if (!entryHasFile(doc)) return null;
    return {
      name: resolveDocumentFileName(doc) ?? '',
      uploadedAt: doc.createdAt ?? null,
    };
  }

  /**
   * Menu offered on the shared card. Preview/download only when the record actually carries a
   * file — the pre-refactor buttons had the same entryHasFile gate, and offering them on a
   * file-less record just clicks through to an error notify. History is always reachable;
   * archive keeps the same create-permission gate as before.
   */
  documentCardMenu(doc: Entry): DocumentCardMenu {
    const hasFile = entryHasFile(doc);
    return {
      preview: hasFile,
      download: hasFile,
      history: true,
      archive: this.canArchiveDocument(),
    };
  }

  downloadDocument(doc: Entry): void {
    this.#documentActions.download(doc, this.documentModuleId());
  }

  previewDocument(doc: Entry): void {
    this.#documentActions.preview(doc, this.documentTitle(doc), this.documentModuleId());
  }

  openDocumentHistory(doc: Entry): void {
    const moduleId = this.documentModuleId();
    if (!moduleId || !doc.id) return;
    openHistoryDialog(this.#dialog, { moduleId, entryId: doc.id, title: this.documentTitle(doc) });
  }

  archiveDocument(doc: Entry): void {
    const entity = this.#modulesStore.enabledEntities().find(module => module.code === DOCUMENT_MODULE_CODE);
    if (!entity || !doc.id || !this.canArchiveDocument()) return;
    this.#entriesService.confirmArchive(doc, entity).subscribe();
  }

  /**
   * Also a `computed`: `documentCardMenu` calls it once per document row, and its inputs are
   * both signals (`documentModuleId` and the store's `currentUserPermissions`), so the
   * permission scan still re-runs the moment a takeover changes the roles.
   */
  readonly canArchiveDocument = computed<boolean>(() => {
    const moduleId = this.documentModuleId();
    return !!moduleId && this.#userStore.checkIfHasCreatePermission(moduleId);
  });

  /* ── Lifecycle ────────────────────────────────────────────────────────── */

  ngOnInit(): void {
    // Seed the parent-aanmelding signal from `field.props.aanmelding` (review-inbox panel).
    // We deliberately avoid Formly's `options.formState` — it isn't reliably
    // propagated to nested FieldType instances and was returning undefined
    // here. `props` is per-field and always synchronously available.
    // On the detail page this prop does not exist; `aanmelding()` then falls back to the
    // optional DetailsService (see #detailsService above).
    const aanmeldingFromProps = (this.field?.props?.['aanmelding'] as Entry | undefined) ?? null;
    this.#aanmeldingState.set(aanmeldingFromProps);

    // Seed the embedded onboarding-entry from the field-control / defaultValue.
    const initial = (this.formControl?.value ?? this.field?.defaultValue ?? null) as Entry | null;
    this.#onboardingValue.set(initial);

    // The list-endpoint trims the embedded `entry` payload — if we only have
    // an id, fetch the full onboarding-entry detail so section 2 can render
    // the wizard answers.
    if (initial?.id && !initial.entry) {
      this.#loadOnboardingDetail(initial.id);
    }
    this.#resolveOnboardingForm();
    this.#resolveOnboardingEntry();

    // Kick off the documents fetch explicitly. Doing it here (rather than via
    // an effect that tracks signals) avoids any uncertainty about effect
    // timing relative to FieldType property wiring.
    this.#refreshDocuments();

    // React to subsequent formControl changes (e.g. parent re-bind).
    if (this.formControl) {
      this.formControl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(value => {
        this.#onboardingValue.set((value as Entry) ?? null);
        if (value?.id && !value.entry) {
          this.#loadOnboardingDetail(value.id);
        }
        this.#resolveOnboardingForm();
        this.#resolveOnboardingEntry();
        this.#refreshDocuments();
      });
    }
  }

  #refreshDocuments(): void {
    const generation = ++this.#documentsRequestGeneration;
    const userId = this.aanmelding()?.entry?.['user']?.id ?? this.onboardingEntry()?.entry?.['user']?.id;
    debugLog('OnboardingOverview', 'documents.refresh', {
      aanmeldingId: this.aanmelding()?.id,
      userId,
    });
    // Set the scope signals so the `documents` computed knows what to filter
    // for once the store-load resolves (or immediately, if the store already
    // holds matching entries from a prior load).
    this.#docsScopeUserId.set(userId ?? null);
    this.#documentsLoadSucceeded.set(false);
    this.#lastSyncedPendingCount.set(null);
    if (!userId || !this.documentModuleId()) {
      this.documentsLoading.set(false);
      return;
    }
    this.#loadDocuments(userId, generation);
  }

  #loadOnboardingDetail(entryId: string): void {
    // The onboarding module is a `page`, so it is absent from `enabledEntities()` (entity/module
    // only) — look it up across every enabled module instead.
    const moduleId = this.#modulesStore.enabledModules().find(m => m.code === ONBOARDING_ENTRY_MODULE_CODE)?.id;
    if (!moduleId) return;
    this.#entriesService
      .getEntry(moduleId, entryId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: full => {
          this.#onboardingValue.set(full ?? null);
          this.#resolveOnboardingForm();
        },
        error: err => console.error('[onboarding-overview] Failed to load onboarding-entry detail:', err),
      });
  }

  /**
   * Recover the onboarding-entry when the aanmelding has no `user-onboarding` value.
   *
   * The link between an aanmelding and the onboarding the carrier actually filled in is written
   * on the onboarding-entry side (`entry['onboarding-aanmelding-id']`); the forward link on the
   * aanmelding stays empty on most records. Without a value the field renders "nog niet
   * ingevuld" even though a completed onboarding exists, so resolve it the other way around.
   *
   * The backend 500s on a `$filter` over the hyphenated `entry.onboarding-aanmelding-id` key, so
   * scope the query by user and match the aanmelding-id client-side. Only an unambiguous result
   * is accepted: an explicit aanmelding-id match, or the user's single onboarding-entry.
   */
  #resolveOnboardingEntry(): void {
    if (this.onboardingEntry()?.id) return; // the field already carries the link

    const aanmelding = this.aanmelding();
    const userId = aanmelding?.entry?.['user']?.id as string | undefined;
    if (!aanmelding?.id || !userId || this.#lookedUpAanmeldingId() === aanmelding.id) return;

    const moduleId = this.#modulesStore.enabledModules().find(m => m.code === ONBOARDING_ENTRY_MODULE_CODE)?.id;
    if (!moduleId) {
      debugLog('OnboardingOverview', 'entry.lookup.skipped', 'no onboarding-entry module id');
      return;
    }

    this.#lookedUpAanmeldingId.set(aanmelding.id);
    this.#formFetchState.set('loading');
    debugLog('OnboardingOverview', 'entry.lookup.start', { moduleId, userId, aanmeldingId: aanmelding.id });
    this.#entriesService
      .getEntries({
        moduleId,
        offset: 0,
        limit: 20,
        filter: `entry.user.id eq '${userId}'`,
        applyQueryParams: false,
      })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: entries => {
          const list = (entries ?? []) as Entry[];
          const match =
            list.find(e => e.entry?.['onboarding-aanmelding-id'] === aanmelding.id) ??
            (list.length === 1 ? list[0] : undefined);
          debugLog('OnboardingOverview', 'entry.lookup.done', { found: list.length, matched: match?.id ?? null });
          this.#formFetchState.set('idle');
          if (!match) return;
          this.#onboardingValue.set(match);
          this.#resolveOnboardingForm();
        },
        error: err => {
          console.error('[onboarding-overview] Onboarding-entry opzoeken mislukt:', err);
          this.#formFetchState.set('failed');
        },
      });
  }

  /**
   * Make sure section 2 has a `generatedForm` to render.
   *
   * `entry['onboarding-form']` comes in two shapes: a full onboarding-forms envelope
   * (`entry.form.generatedForm` present) on aanmeldingen created through the older flow, and a
   * thin `{ id, selectedInput }` reference on newer ones — the detail endpoint does not hydrate
   * it either. Fetch the onboarding-forms entry by id in the thin case, mirroring the fallback
   * the public onboarding-status page already uses (#20715).
   */
  #resolveOnboardingForm(): void {
    const formRef = this.onboardingEntry()?.entry?.['onboarding-form'];
    const embedded = formRef?.entry?.form?.generatedForm;
    if (Array.isArray(embedded) && embedded.length > 0) {
      this.#formFetchState.set('idle');
      return;
    }

    const formId = formRef?.id as string | undefined;
    if (!formId || this.#fetchedFormId() === formId) return;

    const formsModuleId = this.#modulesStore.getModuleByCode(ONBOARDING_FORMS_MODULE_CODE)?.id;
    if (!formsModuleId) {
      debugLog('OnboardingOverview', 'form.fetch.skipped', 'no onboarding-forms module id');
      return;
    }

    this.#fetchedFormId.set(formId);
    this.#formFetchState.set('loading');
    debugLog('OnboardingOverview', 'form.fetch.start', { formsModuleId, formId });
    this.#entriesService
      .getEntry(formsModuleId, formId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: formEntry => {
          const generatedForm = formEntry?.entry?.form?.generatedForm;
          if (!Array.isArray(generatedForm) || generatedForm.length === 0) {
            console.warn('[onboarding-overview] Onboarding-formulier zonder generatedForm:', formId);
            this.#formFetchState.set('failed');
            return;
          }
          this.#fetchedGeneratedForm.set(generatedForm);
          this.#formFetchState.set('idle');
        },
        error: err => {
          console.error('[onboarding-overview] Onboarding-formulier ophalen mislukt:', err);
          this.#formFetchState.set('failed');
        },
      });
  }
}
